import { useState, useEffect, useRef } from "react";
import { ChevronsUpDown, Loader2, Package } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { ProductThumb } from "@/components/common/ProductThumb";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";

export function ProductCombobox({ lineType, value, label, imageUrl, customerId, onSelect, testId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", {
          params: { q: term, types: "product", product_type: lineType, customer_id: customerId || "", limit: 1000 },
        });
        const g = (data.groups || []).find((x) => x.type === "product");
        if (id === reqId.current) setItems(g ? g.items : []);
      } catch {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 160);
    return () => clearTimeout(timer);
  }, [q, open, lineType, customerId]);
  const pick = (it) => { onSelect(it); setOpen(false); setQ(""); };
  const term = q.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn("h-9 w-full justify-between gap-2 font-normal", !label && "text-muted-foreground")}
        >
          <span className="flex min-w-0 items-center gap-2">
            {label && <ProductThumb path={imageUrl} alt={label} size={22} />}
            <span className="truncate">{label || t("quotes.select_product")}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            value={q}
            onValueChange={setQ}
            placeholder={t("search.placeholder_product")}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common.loading")}
              </div>
            )}
            {!loading && !term && (
              <div className="py-6 text-center text-sm text-muted-foreground">{t("search.hint")}</div>
            )}
            {!loading && term && items.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{t("search.empty")}</div>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.id}
                    value={it.id}
                    onSelect={() => pick(it)}
                    data-testid={`${testId}-option`}
                    className="flex items-center gap-3"
                  >
                    <ProductThumb path={it.image_url} alt={it.title} size={32} />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{it.title}</span>
                      {it.subtitle && <span className="truncate text-xs text-muted-foreground">{it.subtitle}</span>}
                    </div>
                    {(it.effective_price || it.selling_price) && (
                      <span className="shrink-0 text-right text-xs tabular-nums">
                        <span className="font-medium">{(it.effective_price || it.selling_price)} €</span>
                        {it.price_explanation && it.price_explanation !== "List price" && (
                          <span className="block text-[10px] text-emerald-600">{it.price_explanation}</span>
                        )}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
